


export enum NotificationType {
  MessageReceived = 1,

  ApplicationReceived = 10,
  ApplicationStatus = 11,
  ApplicationShortlisted = 12,
  ApplicationRejected = 13,

  CollaborationInvite = 20,
  CollaborationAccepted = 21,
  CollaborationDeclined = 22,
  CollaborationUpdate = 23,
  CollaborationCompleted = 24,
  MilestoneSubmitted = 25,
  MilestoneApproved = 26,
  MilestoneRejected = 27,

  PaymentReceived = 30,
  PaymentReleased = 31,
  PaymentPending = 32,
  EscrowCreated = 33,
  EscrowReleased = 34,

  ReviewReceived = 40,
  ReviewReminder = 41,

  ListingViewed = 50,
  ListingExpiring = 51,
  ListingExpired = 52,
  ListingFeatured = 53,

  ProfileViewed = 60,
  FollowReceived = 61,
  ConnectionRequest = 62,
  ConnectionAccepted = 63,
  ProfileViewDigest = 64,
  MediaUploaded = 65,
  MediaEngagementMilestone = 66,
  MediaEngagementDigest = 67,
  JobViewed = 68,
  JobViewDigest = 69,

  JobPosted = 70,
  HiringOpened = 71,
  RentalListed = 72,
  CollabPosted = 73,
  PartnershipOffered = 74,

  CommunityPostJob = 75,
  CommunityPostRent = 76,
  CommunityPostCollab = 77,

  SystemAnnouncement = 100,
  SecurityAlert = 101,
  AccountUpdate = 102,
  MaintenanceScheduled = 103,
}

export enum NotificationPriority {
  Low = 0,
  Normal = 1,
  High = 2,
  Urgent = 3,
}


export interface Notification {
  id: string;
  type: NotificationType;
  typeKey: string;
  priority: NotificationPriority;
  title: string;
  body: string;
  actionUrl?: string;
  referenceId?: string;
  referenceType?: string;
  actor?: NotificationActor;
  
  imageUrl?: string;
  metadata: Record<string, string>;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  timeAgo: string;
  icon: string;
  color: string;
}

export interface NotificationActor {
  id: string;
  name: string;
  avatarUrl?: string;
  initials?: string;
  role?: string;
}


export interface NotificationListResponse {
  items: Notification[];
  totalCount: number;
  unreadCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface UnreadCountResponse {
  count: number;
  highPriorityCount: number;
  countByType: Record<number, number>;
}


export interface NotificationQueryParams {
  page?: number;
  pageSize?: number;
  isRead?: boolean;
  type?: NotificationType;
  minPriority?: NotificationPriority;
}

export interface MarkReadRequest {
  ids: string[];
}

export interface DeleteNotificationsRequest {
  ids: string[];
}


export interface NotificationSignalPayload {
  id: string;
  type: string;
  title: string;
  body: string;
  actionUrl?: string;
  referenceId?: string;
  referenceType?: string;
  actor?: {
    id: string;
    name: string;
    avatarUrl?: string;
    initials?: string;
    role?: string;
  };
  
  imageUrl?: string;
  priority: number;
  createdAt: string;
}

export interface NotificationReadPayload {
  id: string;
  readAt: string;
}

export interface NotificationBulkReadPayload {
  ids: string[];
  readAt: string;
}


export const NotificationEvents = {
  NEW: 'notification_new',
  READ: 'notification_read',
  READ_ALL: 'notification_read_all',
  BULK_READ: 'notification_bulk_read',
  DELETED: 'notification_deleted',
  UNREAD_COUNT: 'notification_unread_count',
  PROFILE_VIEWED: 'profile_viewed',
  MEDIA_MILESTONE: 'media_milestone',
} as const;


export interface ProfileViewedSignalPayload {
  notificationId: string;
  creatorProfileId: string;
  viewerUserId: string;
  viewerIndustry: string | null;
  occurredAt: string;
}

export interface MediaMilestoneSignalPayload {
  notificationId: string;
  mediaId: string;
  mediaKind: 'Portfolio' | 'CommunityPost' | 'Reel' | 'CoverImage';
  totalCount: number;
  occurredAt: string;
}


export interface NotificationTab {
  id: 'all' | 'unread' | 'mentions';
  label: string;
  count?: number;
}

export interface NotificationGroup {
  id: string;
  type: NotificationType;
  referenceId?: string;
  referenceType?: string;
  count: number;
  latestNotification: Notification;
  notifications: Notification[];
}


export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  [NotificationType.MessageReceived]: 'New Message',
  [NotificationType.ApplicationReceived]: 'New Application',
  [NotificationType.ApplicationStatus]: 'Application Update',
  [NotificationType.ApplicationShortlisted]: 'Application Shortlisted',
  [NotificationType.ApplicationRejected]: 'Application Rejected',
  [NotificationType.CollaborationInvite]: 'Collaboration Invite',
  [NotificationType.CollaborationAccepted]: 'Collaboration Accepted',
  [NotificationType.CollaborationDeclined]: 'Collaboration Declined',
  [NotificationType.CollaborationUpdate]: 'Collaboration Update',
  [NotificationType.CollaborationCompleted]: 'Collaboration Completed',
  [NotificationType.MilestoneSubmitted]: 'Milestone Submitted',
  [NotificationType.MilestoneApproved]: 'Milestone Approved',
  [NotificationType.MilestoneRejected]: 'Milestone Rejected',
  [NotificationType.PaymentReceived]: 'Payment Received',
  [NotificationType.PaymentReleased]: 'Payment Released',
  [NotificationType.PaymentPending]: 'Payment Pending',
  [NotificationType.EscrowCreated]: 'Escrow Created',
  [NotificationType.EscrowReleased]: 'Escrow Released',
  [NotificationType.ReviewReceived]: 'New Review',
  [NotificationType.ReviewReminder]: 'Review Reminder',
  [NotificationType.ListingViewed]: 'Listing Viewed',
  [NotificationType.ListingExpiring]: 'Listing Expiring',
  [NotificationType.ListingExpired]: 'Listing Expired',
  [NotificationType.ListingFeatured]: 'Listing Featured',
  [NotificationType.ProfileViewed]: 'Profile Viewed',
  [NotificationType.FollowReceived]: 'New Follower',
  [NotificationType.ConnectionRequest]: "Let's Cook! Request",
  [NotificationType.ConnectionAccepted]: "Let's Cook! Accepted",
  [NotificationType.ProfileViewDigest]: 'Profile Views Today',
  [NotificationType.MediaUploaded]: 'New Media Published',
  [NotificationType.MediaEngagementMilestone]: 'Engagement Milestone',
  [NotificationType.MediaEngagementDigest]: 'Engagement Summary',

  [NotificationType.JobViewed]: 'Job Viewed',
  [NotificationType.JobViewDigest]: 'Job View Summary',

  [NotificationType.JobPosted]: 'New Job Posted',
  [NotificationType.HiringOpened]: 'Hiring Opened',
  [NotificationType.RentalListed]: 'New Rental Listing',
  [NotificationType.CollabPosted]: 'New Collaboration',
  [NotificationType.PartnershipOffered]: 'Partnership Offered',

  [NotificationType.CommunityPostJob]: 'Community Hiring Post',
  [NotificationType.CommunityPostRent]: 'Community Rental Post',
  [NotificationType.CommunityPostCollab]: 'Community Collab Post',

  [NotificationType.SystemAnnouncement]: 'Announcement',
  [NotificationType.SecurityAlert]: 'Security Alert',
  [NotificationType.AccountUpdate]: 'Account Update',
  [NotificationType.MaintenanceScheduled]: 'Maintenance',
};

export const NOTIFICATION_CATEGORY_COLORS: Record<string, string> = {
  Messaging: '#3b82f6',
  Applications: '#8b5cf6',
  Collaborations: '#6366f1',
  Payments: '#10b981',
  Reviews: '#f59e0b',
  Listings: '#ec4899',
  Social: '#14b8a6',
  System: '#6b7280',
};

export const NOTIFICATION_CATEGORY_ICONS: Record<string, string> = {
  Messaging: 'message-circle',
  Applications: 'file-text',
  Collaborations: 'users',
  Payments: 'credit-card',
  Reviews: 'star',
  Listings: 'briefcase',
  Social: 'user',
  System: 'info',
};
